import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Tab,
  Tabs,
  useTheme,
  alpha,
  styled,
  Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatLocationDisplay } from '../utils/formatting';
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 600,
}));

const FilterBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const CategoryTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const EventCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const EventDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const PriceTag = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  borderRadius: '4px',
  padding: theme.spacing(0.5, 1.5),
  fontWeight: 'bold',
  zIndex: 1,
}));

const OrganizationCard = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  overflow: 'hidden',
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
}));

const CoverImage = styled(Box)(({ theme }) => ({
  height: 200,
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 1
  }
}));

const OrgInfo = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(3),
  color: 'white',
  zIndex: 2
}));

const OrgLogo = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 200,
  borderRadius: theme.shape.borderRadius * 2,
  border: '4px solid white',
  marginTop: theme.spacing(-8),
  position: 'relative',
  zIndex: 5,
  boxShadow: theme.shadows[2]
}));

const OrgDetails = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const DateChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  fontWeight: 'bold',
  zIndex: 1,
}));

// Define interfaces
interface Event {
  id: string;
  title: string;
  description: string;
  startDate: Date;
  endDate?: Date;
  location: string;
  imageUrl: string;
  price: number;
  ticketTypes: TicketType[];
  capacity: number;
  registeredCount: number;
  category: string;
}

interface TicketType {
  id: string;
  name: string;
  price: number;
  description?: string;
  available: boolean;
}

interface Organization {
  id: string;
  name: string;
  logo: string;
  coverImage?: string;
  description: string;
  website?: string;
  location?: string;
  memberCount?: number;
  foundedYear?: number;
  verified: boolean;
  events: Event[];
}

// Event categories
const categories: Record<string, string> = {
  'all': 'All Categories',
  'outdoor': 'Outdoor Activities',
  'workshop': 'Workshops & Classes',
  'travel': 'Travel & Tours',
  'food': 'Food & Dining',
  'culture': 'Arts & Culture',
  'social': 'Social Events',
  'business': 'Business & Networking',
  'sports': 'Sports & Fitness'
};

// Function to get all categories from organizations
const getAllCategories = (organizations: Organization[]): string[] => {
  const categoriesSet = new Set<string>();
  organizations.forEach(org => {
    org.events.forEach(event => {
      if (event.category) {
        categoriesSet.add(event.category);
      }
    });
  });
  return Array.from(categoriesSet);
};

// Mock data for organizations and their events
const mockOrganizations: Organization[] = [
  {
    id: 'org1',
    name: 'Adventure Seekers',
    logo: 'https://picsum.photos/seed/org1/200/200',
    coverImage: 'https://picsum.photos/seed/org1cover/1200/300',
    description: 'Adventure Seekers connects outdoor enthusiasts with thrilling experiences around the world. From hiking in the Alps to kayaking in the Amazon, we curate unforgettable adventures for all skill levels.',
    website: 'www.adventureseekers.com',
    location: 'Denver, CO',
    memberCount: 12500,
    foundedYear: 2015,
    verified: true,
    events: [
      {
        id: 'event1',
        title: 'Summer Hiking Adventure',
        description: 'Join us for a day of hiking through the beautiful trails of Rocky Mountain National Park. This event is suitable for all experience levels.',
        startDate: new Date(2025, 5, 15, 9, 0), // June 15, 2025, 9:00 AM
        endDate: new Date(2025, 5, 15, 16, 0), // June 15, 2025, 4:00 PM
        location: 'Rocky Mountain National Park',
        imageUrl: 'https://picsum.photos/seed/event1/600/400',
        price: 25,
        ticketTypes: [
          {
            id: 'ticket1',
            name: 'Standard Pass',
            price: 25,
            description: 'Access to the guided hiking tour and lunch',
            available: true
          },
          {
            id: 'ticket2',
            name: 'Premium Pass',
            price: 40,
            description: 'Includes equipment rental, guided tour, and lunch',
            available: true
          }
        ],
        capacity: 50,
        registeredCount: 32,
        category: 'outdoor'
      },
      {
        id: 'event2',
        title: 'Mountain Biking Expedition',
        description: 'Experience the thrill of mountain biking through scenic trails with experienced guides.',
        startDate: new Date(2025, 6, 8, 10, 0), // July 8, 2025, 10:00 AM
        endDate: new Date(2025, 6, 8, 15, 0), // July 8, 2025, 3:00 PM
        location: 'Elk Mountain Trails',
        imageUrl: 'https://picsum.photos/seed/event2/600/400',
        price: 35,
        ticketTypes: [
          {
            id: 'ticket3',
            name: 'Bike Trail Pass',
            price: 35,
            description: 'Access to mountain biking trails with guide',
            available: true
          },
          {
            id: 'ticket4',
            name: 'Bike Rental Package',
            price: 65,
            description: 'Includes bike rental, helmet, and trail pass',
            available: true
          }
        ],
        capacity: 30,
        registeredCount: 14,
        category: 'outdoor'
      },
      {
        id: 'event3',
        title: 'Introduction to Rock Climbing',
        description: 'Learn the basics of rock climbing in this beginner-friendly workshop with certified instructors.',
        startDate: new Date(2025, 6, 22, 13, 0), // July 22, 2025, 1:00 PM
        endDate: new Date(2025, 6, 22, 17, 0), // July 22, 2025, 5:00 PM
        location: 'Clear Creek Canyon',
        imageUrl: 'https://picsum.photos/seed/event3/600/400',
        price: 45,
        ticketTypes: [
          {
            id: 'ticket5',
            name: 'Climbing Workshop',
            price: 45,
            description: 'Beginner rock climbing workshop with equipment',
            available: true
          }
        ],
        capacity: 20,
        registeredCount: 15,
        category: 'workshop'
      }
    ]
  },
  {
    id: 'org2',
    name: 'City Explorers',
    logo: 'https://picsum.photos/seed/org2/200/200',
    coverImage: 'https://picsum.photos/seed/org2cover/1200/300',
    description: 'Discover hidden gems in urban environments with City Explorers. Our curated city guides reveal the secrets of metropolitan areas worldwide that most tourists never see.',
    website: 'www.cityexplorers.com',
    location: 'New York, NY',
    memberCount: 8700,
    foundedYear: 2018,
    verified: true,
    events: [
      {
        id: 'event4',
        title: 'Historic Downtown Walking Tour',
        description: 'Explore the rich history of downtown with knowledgeable local guides who will reveal hidden stories behind famous landmarks.',
        startDate: new Date(2025, 5, 20, 10, 0), // June 20, 2025, 10:00 AM
        endDate: new Date(2025, 5, 20, 12, 30), // June 20, 2025, 12:30 PM
        location: 'City Center',
        imageUrl: 'https://picsum.photos/seed/event4/600/400',
        price: 15,
        ticketTypes: [
          {
            id: 'ticket6',
            name: 'Tour Ticket',
            price: 15,
            description: 'Standard walking tour ticket',
            available: true
          },
          {
            id: 'ticket7',
            name: 'Tour + Lunch',
            price: 35,
            description: 'Tour ticket with lunch at a historic restaurant',
            available: true
          }
        ],
        capacity: 40,
        registeredCount: 22,
        category: 'travel'
      },
      {
        id: 'event5',
        title: 'Street Art Discovery Tour',
        description: 'Join our expert guides to discover the vibrant street art scene and learn about the artists behind the murals.',
        startDate: new Date(2025, 6, 15, 14, 0), // July 15, 2025, 2:00 PM
        endDate: new Date(2025, 6, 15, 16, 30), // July 15, 2025, 4:30 PM
        location: 'Arts District',
        imageUrl: 'https://picsum.photos/seed/event5/600/400',
        price: 20,
        ticketTypes: [
          {
            id: 'ticket8',
            name: 'Standard Tour',
            price: 20,
            description: 'Street art tour with guide',
            available: true
          }
        ],
        capacity: 30,
        registeredCount: 12,
        category: 'culture'
      },
      {
        id: 'event6',
        title: 'Night Photography Workshop',
        description: 'Learn how to capture stunning city nightscapes in this hands-on photography workshop.',
        startDate: new Date(2025, 7, 5, 20, 0), // August 5, 2025, 8:00 PM
        endDate: new Date(2025, 7, 5, 23, 0), // August 5, 2025, 11:00 PM
        location: 'Skyline Viewpoint',
        imageUrl: 'https://picsum.photos/seed/event6/600/400',
        price: 40,
        ticketTypes: [
          {
            id: 'ticket9',
            name: 'Workshop Ticket',
            price: 40,
            description: 'Night photography workshop (bring your own camera)',
            available: true
          }
        ],
        capacity: 15,
        registeredCount: 8,
        category: 'workshop'
      }
    ]
  },
  {
    id: 'org3',
    name: 'Photography Masters',
    logo: 'https://picsum.photos/seed/org3/200/200',
    coverImage: 'https://picsum.photos/seed/org3cover/1200/300',
    description: 'Learn photography from award-winning professionals. Our instructors have been published in National Geographic, Time, and other prestigious publications.',
    website: 'www.photographymasters.com',
    location: 'San Francisco, CA',
    memberCount: 5600,
    foundedYear: 2016,
    verified: false,
    events: [
      {
        id: 'event7',
        title: 'Photography Workshop: Urban Landscapes',
        description: 'Master the art of urban landscape photography in this hands-on workshop led by professional photographers.',
        startDate: new Date(2025, 5, 22, 14, 0), // June 22, 2025, 2:00 PM
        endDate: new Date(2025, 5, 22, 18, 0), // June 22, 2025, 6:00 PM
        location: 'Downtown Art Center',
        imageUrl: 'https://picsum.photos/seed/event7/600/400',
        price: 45,
        ticketTypes: [
          {
            id: 'ticket10',
            name: 'Workshop Pass',
            price: 45,
            description: 'Full workshop access with professional guidance',
            available: true
          },
          {
            id: 'ticket11',
            name: 'Workshop + Portfolio Review',
            price: 65,
            description: 'Workshop access plus one-on-one portfolio review',
            available: true
          }
        ],
        capacity: 25,
        registeredCount: 18,
        category: 'workshop'
      },
      {
        id: 'event8',
        title: 'Portrait Photography Masterclass',
        description: 'Learn advanced portrait photography techniques from award-winning photographers.',
        startDate: new Date(2025, 6, 12, 10, 0), // July 12, 2025, 10:00 AM
        endDate: new Date(2025, 6, 12, 16, 0), // July 12, 2025, 4:00 PM
        location: 'Photography Studio',
        imageUrl: 'https://picsum.photos/seed/event8/600/400',
        price: 75,
        ticketTypes: [
          {
            id: 'ticket12',
            name: 'Masterclass',
            price: 75,
            description: 'Full-day portrait photography masterclass',
            available: true
          }
        ],
        capacity: 15,
        registeredCount: 10,
        category: 'workshop'
      }
    ]
  },
  {
    id: 'org4',
    name: 'Local Food Explorers',
    logo: 'https://picsum.photos/seed/org4/200/200',
    coverImage: 'https://picsum.photos/seed/org4cover/1200/300',
    description: 'Experience authentic local cuisine around the world. We connect food lovers with authentic culinary experiences, from street food tours to cooking classes with local chefs.',
    website: 'www.localfoodexplorers.com',
    location: 'Chicago, IL',
    memberCount: 7300,
    foundedYear: 2017,
    verified: true,
    events: [
      {
        id: 'event9',
        title: 'Food Festival Tasting Pass',
        description: 'Sample a variety of cuisines from top local restaurants at our annual food festival.',
        startDate: new Date(2025, 5, 30, 12, 0), // June 30, 2025, 12:00 PM
        endDate: new Date(2025, 5, 30, 20, 0), // June 30, 2025, 8:00 PM
        location: 'City Food Hall',
        imageUrl: 'https://picsum.photos/seed/event9/600/400',
        price: 35,
        ticketTypes: [
          {
            id: 'ticket13',
            name: 'Tasting Pass',
            price: 35,
            description: 'Access to all food stalls with 8 tasting tickets',
            available: true
          },
          {
            id: 'ticket14',
            name: 'VIP Tasting Pass',
            price: 65,
            description: 'Early access, 15 tasting tickets, and exclusive dishes',
            available: true
          }
        ],
        capacity: 500,
        registeredCount: 320,
        category: 'food'
      },
      {
        id: 'event10',
        title: 'Authentic Italian Cooking Class',
        description: 'Learn to prepare authentic Italian dishes with ingredients and techniques from different regions of Italy.',
        startDate: new Date(2025, 6, 10, 18, 0), // July 10, 2025, 6:00 PM
        endDate: new Date(2025, 6, 10, 21, 0), // July 10, 2025, 9:00 PM
        location: 'Culinary School',
        imageUrl: 'https://picsum.photos/seed/event10/600/400',
        price: 50,
        ticketTypes: [
          {
            id: 'ticket15',
            name: 'Cooking Class',
            price: 50,
            description: 'Hands-on cooking class with dinner and wine',
            available: true
          }
        ],
        capacity: 20,
        registeredCount: 15,
        category: 'food'
      },
      {
        id: 'event11',
        title: 'Street Food Walking Tour',
        description: 'Discover hidden food gems and sample diverse street food options with knowledgeable local guides.',
        startDate: new Date(2025, 7, 8, 11, 0), // August 8, 2025, 11:00 AM
        endDate: new Date(2025, 7, 8, 14, 0), // August 8, 2025, 2:00 PM
        location: 'Market District',
        imageUrl: 'https://picsum.photos/seed/event11/600/400',
        price: 40,
        ticketTypes: [
          {
            id: 'ticket16',
            name: 'Tour Ticket',
            price: 40,
            description: 'Guided tour with food tastings included',
            available: true
          }
        ],
        capacity: 25,
        registeredCount: 18,
        category: 'food'
      }
    ]
  },
  {
    id: 'org5',
    name: 'Cultural Heritage Tours',
    logo: 'https://picsum.photos/seed/org5/200/200',
    coverImage: 'https://picsum.photos/seed/org5cover/1200/300',
    description: 'Explore the rich cultural heritage of different regions with expert historians and local guides. Our tours bring history to life through immersive experiences.',
    website: 'www.culturalheritagetours.com',
    location: 'Boston, MA',
    memberCount: 9100,
    foundedYear: 2014,
    verified: true,
    events: [
      {
        id: 'event12',
        title: 'Historical Walking Tour',
        description: 'Step back in time with our expert guides as you explore the historical landmarks and hidden stories of the Old Town District.',
        startDate: new Date(2025, 7, 5, 10, 0), // August 5, 2025, 10:00 AM
        endDate: new Date(2025, 7, 5, 12, 0), // August 5, 2025, 12:00 PM
        location: 'Old Town District',
        imageUrl: 'https://picsum.photos/seed/event12/600/400',
        price: 15,
        ticketTypes: [
          {
            id: 'ticket17',
            name: 'Tour Pass',
            price: 15,
            description: 'Standard historical walking tour',
            available: true
          },
          {
            id: 'ticket18',
            name: 'Family Pass',
            price: 40,
            description: 'Tour for 2 adults and up to 3 children',
            available: true
          }
        ],
        capacity: 30,
        registeredCount: 12,
        category: 'culture'
      },
      {
        id: 'event13',
        title: 'Museum After Hours: Special Exhibition',
        description: 'Exclusive after-hours access to the museum\'s latest exhibition with curator talks and wine reception.',
        startDate: new Date(2025, 7, 15, 19, 0), // August 15, 2025, 7:00 PM
        endDate: new Date(2025, 7, 15, 22, 0), // August 15, 2025, 10:00 PM
        location: 'Metropolitan Museum',
        imageUrl: 'https://picsum.photos/seed/event13/600/400',
        price: 30,
        ticketTypes: [
          {
            id: 'ticket19',
            name: 'After Hours Ticket',
            price: 30,
            description: 'Special exhibition access with reception',
            available: true
          }
        ],
        capacity: 100,
        registeredCount: 65,
        category: 'culture'
      }
    ]
  }
];

// Component implementation
const ExploreEventsPage: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Initialize with mock data
  useEffect(() => {
    // Set filtered organizations to all organizations initially
    setFilteredOrganizations(mockOrganizations);
    setLoading(false);
    
    // In a real app, you would fetch actual data from Firestore here
  }, [currentUser]);

  // Filter organizations based on category and search query
  useEffect(() => {
    // Start with all organizations
    let filtered = [...mockOrganizations];
    
    // For each organization, filter its events first based on the active category
    filtered = filtered.map(org => {
      // Filter events based on category
      let filteredEvents = [...org.events];
      
      if (activeCategory !== 'all') {
        filteredEvents = filteredEvents.filter(event => 
          event.category === activeCategory
        );
      }
      
      // Filter events based on search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredEvents = filteredEvents.filter(event => 
          event.title.toLowerCase().includes(query) || 
          event.description.toLowerCase().includes(query) ||
          event.location.toLowerCase().includes(query)
        );
      }
      
      // Sort events by date (earliest first)
      filteredEvents.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
      
      // Limit to 3 upcoming events per organization
      const upcomingEvents = filteredEvents.filter(event => 
        event.startDate > new Date()
      ).slice(0, 3);
      
      // Return organization with filtered events
      return {
        ...org,
        events: upcomingEvents
      };
    });
    
    // Finally, filter out organizations that have no events after filtering
    filtered = filtered.filter(org => org.events.length > 0);
    
    // If search query is for organizations
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const orgsMatchingQuery = filtered.filter(org => 
        org.name.toLowerCase().includes(query) || 
        org.description.toLowerCase().includes(query)
      );
      
      // If we found orgs matching query, use those. Otherwise keep the events-based filtered list
      if (orgsMatchingQuery.length > 0) {
        filtered = orgsMatchingQuery;
      }
    }
    
    setFilteredOrganizations(filtered);
  }, [activeCategory, searchQuery]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveCategory(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <PageContainer>
      <PageTitle variant="h4">Explore Events</PageTitle>
      
      <FilterBar elevation={1}>
        <SearchField
          fullWidth
          placeholder="Search events or organizations..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <CategoryTabs
          value={activeCategory}
          onChange={handleCategoryChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab key="all" label="All Categories" value="all" />
          {getAllCategories(mockOrganizations).map((category) => (
            <Tab key={category} label={categories[category] || category} value={category} />
          ))}
        </CategoryTabs>
      </FilterBar>
      
      {loading ? (
        <Typography>Loading events...</Typography>
      ) : filteredOrganizations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No events found matching your criteria.
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
            Try changing your search terms or category filter.
          </Typography>
        </Paper>
      ) : (
        <>
          {filteredOrganizations.map((organization) => (
            <Box key={organization.id} mb={6}>
              <OrganizationCard elevation={3}>
                {/* Cover Image */}
                <CoverImage
                  sx={{
                    backgroundImage: `url(${organization.coverImage || 'https://picsum.photos/seed/cover/1200/300'})`
                  }}
                >
                  <OrgInfo>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mr: 1 }}>
                        {organization.name}
                      </Typography>
                      {organization.verified && (
                        <VerifiedIcon 
                          color="primary" 
                          fontSize="medium" 
                          titleAccess="Verified Organization" 
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                      {organization.location && (
                        <Typography variant="body1">
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ mr: 0.5 }}>📍</Box> {organization.location}
                          </Box>
                        </Typography>
                      )}
                      {organization.memberCount && (
                        <Typography variant="body1">
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ mr: 0.5 }}>👥</Box> {organization.memberCount.toLocaleString()} members
                          </Box>
                        </Typography>
                      )}
                    </Box>
                  </OrgInfo>
                </CoverImage>
                
                <OrgDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box 
                        component="img" 
                        src={organization.logo} 
                        alt={organization.name} 
                        sx={{ 
                          width: '100%', 
                          maxWidth: 200, 
                          borderRadius: 2,
                          border: '4px solid white',
                          mt: { xs: 0, md: -8 },
                          position: 'relative',
                          zIndex: 5,
                          boxShadow: 2
                        }} 
                      />
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <Typography variant="body1" paragraph>
                        {organization.description}
                      </Typography>
                      {organization.website && (
                        <Button 
                          variant="text" 
                          color="primary" 
                          startIcon={<Box component="span">🌐</Box>}
                          href={`https://${organization.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ mr: 2 }}
                        >
                          Visit Website
                        </Button>
                      )}
                      <Button 
                        variant="text" 
                        color="primary" 
                        component={Link}
                        to={`/organizations/${organization.id}/events`}
                        sx={{ mr: 2 }}
                      >
                        View All Events
                      </Button>
                    </Grid>
                  </Grid>
                </OrgDetails>
              </OrganizationCard>
              
              {/* Upcoming Events */}
              <SectionHeader>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Upcoming Events
                </Typography>
                {organization.events.length > 3 && (
                  <Button 
                    variant="text" 
                    component={Link}
                    to={`/organizations/${organization.id}/events`}
                    size="small"
                  >
                    View All
                  </Button>
                )}
              </SectionHeader>
              
              <Grid container spacing={3}>
                {organization.events.map((event) => (
                  <Grid item xs={12} sm={6} md={4} key={event.id}>
                    <EventCard>
                      <Box sx={{ position: 'relative' }}>
                        <DateChip 
                          label={formatDate(event.startDate)} 
                          color="default"
                        />
                        {event.price > 0 && (
                          <PriceTag>
                            ${event.price}
                          </PriceTag>
                        )}
                        <CardMedia
                          component="img"
                          height="160"
                          image={event.imageUrl}
                          alt={event.title}
                        />
                      </Box>
                      
                      <CardContent>
                        <Typography variant="h6" component="h3" gutterBottom>
                          {event.title}
                        </Typography>
                        
                        <EventDetail>
                          <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatDate(event.startDate)}
                          </Typography>
                        </EventDetail>
                        
                        <EventDetail>
                          <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatTime(event.startDate)}
                          </Typography>
                        </EventDetail>
                        
                        <EventDetail>
                          <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatLocationDisplay(event.location)}
                          </Typography>
                        </EventDetail>
                        
                        <EventDetail>
                          <PeopleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {event.registeredCount} / {event.capacity} registered
                          </Typography>
                        </EventDetail>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          height: 60, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {event.description}
                        </Typography>
                      </CardContent>
                      
                      <Box sx={{ flexGrow: 1 }} />
                      
                      <CardActions sx={{ p: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          component={Link}
                          to={`/ticket-purchase/${organization.id}/${event.id}`}
                        >
                          Get Tickets
                        </Button>
                      </CardActions>
                    </EventCard>
                  </Grid>
                ))}
              </Grid>
              
              {/* Divider between organizations */}
              {filteredOrganizations.indexOf(organization) < filteredOrganizations.length - 1 && (
                <Divider sx={{ mt: 6 }} />
              )}
            </Box>
          ))}
        </>
      )}
    </PageContainer>
  );
};

export default ExploreEventsPage;
